import { Component, OnInit } from "@angular/core";
import { CustomerService, ImagingService, CompanyService } from "src/app/services";
import { Subscription, forkJoin } from "rxjs";
import { WebCustomer, WebImageInfoRequest, WebSettings, CompanyClient } from "src/app/models";
import { SessionDataService } from "src/app/services/session-data.service";
import { ErrorDialogComponent } from "src/app/components/error-dialog/error-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { filter, take } from "rxjs/operators";

@Component({
  selector: "app-imaging",
  templateUrl: "./imaging.component.html",
  styleUrls: ["./imaging.component.scss"]
})
export class ImagingComponent implements OnInit
{

  customerSubscription: Subscription;
  customer: WebCustomer;
  webSettings: WebSettings;
  interfaceAndWebSettingsSubscription: Subscription;
  searchTexts: string[];
  allImages: any[] = [];
  selectedImage: any = null;
  imagePath: string;
  groupedImages: { [key: string]: any[] } = {};
  image: string;
  searchText: string = "";
  isSearchButtonDisabled: boolean = false;
  noSearchResultsMessage: string = '';
  isArchiveAvailable: boolean;
  showMessage: boolean = false;
  dataLogical: string;
  showSpinner: boolean = false;
  showSearchBar: boolean = false;
  webImageInfoRequest: WebImageInfoRequest = {};

  constructor(
    private imagingService: ImagingService,
    private customerService: CustomerService,
    private companyService: CompanyService,
    private sessionDataService: SessionDataService,
    private companyClient: CompanyClient,
    public dialog: MatDialog,
  )
  {
    this.dataLogical = this.sessionDataService.DataLogical;
  }

  ngOnInit(): void
  {
    this.customerSubscription = this.customerService.customer
      .pipe(filter(customer => !!customer))
      .subscribe(customer =>
      {
        this.customer = customer;
      });

    this.interfaceAndWebSettingsSubscription = forkJoin({
      appStartup: this.companyClient.getAppStartup(this.dataLogical, this.customer.BranchNo),
      webSettings: this.companyService.webSettings.pipe(take(1))
    }).subscribe(({ appStartup, webSettings }) =>
    {
      this.isArchiveAvailable = appStartup.CompanyInterfaces.IsArchiveAvailable ?? true;

      if (webSettings)
      {
        this.webSettings = webSettings;
        this.searchTexts = [];

        if (webSettings.IsImagingEnabled)
        {
          if (webSettings.IsInvoiceViewingEnabled)
          {
            this.searchTexts.push("invoices");
          }
          if (webSettings.IsStatementViewingEnabled)
          {
            this.searchTexts.push("statements");
          }
          if (webSettings.IsLetterViewingEnabled)
          {
            this.searchTexts.push("letters");
          }
        }
      }
      this.loadImages();
    });

  }

  loadImages()
  {
    this.showMessage = true;
    this.noSearchResultsMessage = 'Loading documents...';
    this.showSpinner = true;

    if (this.sessionDataService.IsEmployee)
    {
      this.searchTexts = [""];
    }
    this.webImageInfoRequest = {
      CustId: this.customer.CustId,
      BranchNo: this.customer.BranchNo,
      SearchTexts: this.searchTexts
    }
    if ((this.isArchiveAvailable && this.webSettings.IsImagingEnabled) || (this.isArchiveAvailable && this.sessionDataService.IsEmployee))
    {
      this.imagingService.getDocumentList(this.webImageInfoRequest).subscribe(i =>
      {
        if (i)
        {
          this.showMessage = false;
          this.showSpinner = false;
          this.showSearchBar = true;
          if (this.allImages != null && this.allImages.length > 0)
          {
            this.allImages = []
          }
          if (i.CustImageDocumentList.length > 0)
          {
            this.allImages = i.CustImageDocumentList;
            this.groupImages(this.allImages);
          }
          else if (i.ResponseStatus?.Errors)
          {
            this.showSearchBar = false;
            const errorMessage = 'There was an error fetching documents.'
            this.errorDialog(errorMessage);
          }
          else if (i.CustImageDocumentList.length < 1)
          {
            this.showMessage = true;
            this.showSearchBar = false;
            this.noSearchResultsMessage = 'No documents available.'
          }
        }
        else
        {
          this.showMessage = true;
          this.showSearchBar = false;
          this.noSearchResultsMessage = 'Documents are not available for viewing.'
        }
      });
    }
    else
    {
      this.showSpinner = false;
      this.showMessage = true;
      this.showSearchBar = false;
      this.noSearchResultsMessage = 'Documents are not available.'
    }
  }

  groupImages(images)
  {
    this.showMessage = false;
    this.groupedImages = {};

    images.forEach(image =>
    {
      this.extractMimeTypeAndIcon(image);

      if (!this.groupedImages[image.SubFolderName])
      {
        this.groupedImages[image.SubFolderName] = [];
      }
      this.groupedImages[image.SubFolderName].push(image);
    });

    for (let subFolder in this.groupedImages)
    {
      this.groupedImages[subFolder].sort((a, b) => new Date(b.DateArchived).getTime() - new Date(a.DateArchived).getTime());
    }
    this.showSpinner = false;
  }

  onSearchPressed(searchText: string)
  {
    this.showMessage = false;
    this.showSpinner = true;
    this.showSearchBar = true;
    this.isSearchButtonDisabled = true;
    this.searchText.toLowerCase();
    const filteredImages = this.allImages.filter(image =>
      image.SubFolderName.toLowerCase().includes(searchText) ||
      image.DateArchived.toLowerCase().includes(searchText) ||
      image.DocumentName.toLowerCase().includes(searchText)
    );
    if (filteredImages.length < 1)
    {
      this.showSpinner = false;
      this.showMessage = true;
      this.noSearchResultsMessage = 'No search results found. Please adjust your search and try again.';
    }
    else
    {
      this.groupImages(filteredImages);
    }
    this.isSearchButtonDisabled = false;
  }

  clearSearchResults()
  {
    this.searchText = '';
    this.showMessage = false;
    this.noSearchResultsMessage = '';
    this.loadImages();
  }

  downloadFile(image: any)
  {
    this.selectedImage = image;
    this.imagingService.getDocument(image, this.customer.BranchNo).subscribe(
      d =>
      {
        if (d)
        {
          const base64String = d.StringValue;
          this.image = `data:${ image.mimeType };base64,${ base64String }`;
          const link = document.createElement('a');
          link.href = this.image;
          link.download = this.selectedImage.DocumentName;
          link.click();
        }
        else
        {
          this.errorDialog('There was a problem downloading your document, please try again.')
        }
      },
      (error) =>
      {
        const errorMessage = 'Error fetching document ' + error + "."
        this.errorDialog(errorMessage);
      }
    );
  }

  extractMimeTypeAndIcon(image: any)
  {
    const mimeTypes: { [key: string]: string } = {
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'bmp': 'image/bmp',
      'tiff': 'image/tiff',
      'tif': 'image/tiff',
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    };

    const icons: { [key: string]: string } = {
      'png': 'png',
      'jpg': 'jpeg',
      'jpeg': 'jpeg',
      'gif': 'gif',
      'bmp': 'bmp',
      'tiff': 'tiff',
      'tif': 'tiff',
      'pdf': 'pdf',
      'doc': 'doc',
      'docx': 'doc'
    };
    const extension = image.DocumentExtension.toLowerCase().replace('.', '');
    image.mimeType = mimeTypes[extension] || 'application/pdf';
    image.icon = icons[extension] || 'jpeg';
  }

  errorDialog(error): void
  {
    this.showSpinner = false;
    const dialogRef = this.dialog.open(ErrorDialogComponent, {
      minWidth: "360px",
      data: error,
    });
    dialogRef.afterClosed().subscribe((_) =>
    {
      this.showSpinner = false;
    });
  }

  ngOnDestroy(): void
  {
    this.customerSubscription.unsubscribe();
    this.interfaceAndWebSettingsSubscription.unsubscribe();
  }

}
